import React, { useState, useEffect } from "react";
import {
  listInventory,
  updateStock,
  updateCost,
  updateStringName,
  fetchAdminProfile,
  addString,
  deleteString,
} from "../../api/api";
import styles from "./StringerInventory.module.css";

const StringerInventory = () => {
  const [filter, setFilter] = useState("all");
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [editedCosts, setEditedCosts] = useState({});
  const [updatingCost, setUpdatingCost] = useState({});
  const [editedNames, setEditedNames] = useState({});
  const [editedBrands, setEditedBrands] = useState({});
  const [editedImages, setEditedImages] = useState({});
  const [updatingName, setUpdatingName] = useState({});
  const [userRole, setUserRole] = useState("");
  const [userLocation, setUserLocation] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newString, setNewString] = useState({
    string_name: "",
    brand: "",
    cost_per_unit: "",
    image_url: "",
  });
  const [addingString, setAddingString] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stringToDelete, setStringToDelete] = useState(null);
  const [deletingString, setDeletingString] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem("adminRole");
    setUserRole(role || "");
    fetchUserProfile();
    fetchInventory();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      const response = await fetchAdminProfile(token);
      const profile = response.data;
      setUserLocation(profile.location || "");
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  // Map display location names to backend format
  // No need to normalize - backend now expects exact store names
  // "Jumeirah Store", "Marina Store", "Dubai Hills Store"

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await listInventory();
      console.log("Inventory response:", response.data);
      const filteredData = response.data.filter(
        (item) => item.string_name !== "Bring Own String"
      );
      const sortedData = filteredData.sort((a, b) => a.string_id - b.string_id);
      console.log("Filtered inventory:", sortedData);
      setInventory(sortedData);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      alert("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (stringId, newQuantity) => {
    setEditedValues((prev) => ({
      ...prev,
      [stringId]: newQuantity,
    }));
  };

  const handleCostChange = (stringId, newCost) => {
    setEditedCosts((prev) => ({
      ...prev,
      [stringId]: newCost,
    }));
  };

  const handleNameChange = (stringId, newName) => {
    setEditedNames((prev) => ({
      ...prev,
      [stringId]: newName,
    }));
  };

  const handleBrandChange = (stringId, newBrand) => {
    setEditedBrands((prev) => ({
      ...prev,
      [stringId]: newBrand,
    }));
  };

  const handleImageChange = (stringId, newImage) => {
    setEditedImages((prev) => ({
      ...prev,
      [stringId]: newImage,
    }));
  };

  const handleSaveCost = async (stringId) => {
    console.log("Saving cost for stringId:", stringId);
    const newCost = editedCosts[stringId];
    const cost = parseFloat(newCost);

    if (isNaN(cost) || cost < 0) {
      alert("Please enter a valid cost (0 or greater)");
      return;
    }

    if (!stringId) {
      alert("Error: Invalid string ID");
      console.error("stringId is missing or invalid:", stringId);
      return;
    }

    try {
      setUpdatingCost((prev) => ({ ...prev, [stringId]: true }));

      console.log("Calling updateCost with:", { stringId, cost });
      await updateCost(stringId, cost);

      setInventory((prevInventory) =>
        prevInventory.map((item) =>
          item.string_id === stringId ? { ...item, cost_per_unit: cost } : item
        )
      );

      setEditedCosts((prev) => {
        const newValues = { ...prev };
        delete newValues[stringId];
        return newValues;
      });

      setUpdatingCost((prev) => ({ ...prev, [stringId]: false }));
    } catch (error) {
      console.error("Error updating cost:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update cost";
      alert(`Failed to update cost: ${errorMsg}`);
      setUpdatingCost((prev) => ({ ...prev, [stringId]: false }));
    }
  };

  const handleSaveName = async (stringId) => {
    const item = inventory.find((i) => i.string_id === stringId);
    const newName = editedNames[stringId] !== undefined ? editedNames[stringId] : item.string_name;
    const newBrand = editedBrands[stringId] !== undefined ? editedBrands[stringId] : item.brand;
    const newImage = editedImages[stringId] !== undefined ? editedImages[stringId] : item.image_url;

    if (!newName.trim() || !newBrand.trim()) {
      alert("String name and brand cannot be empty");
      return;
    }

    if (!stringId) {
      alert("Error: Invalid string ID");
      return;
    }

    try {
      setUpdatingName((prev) => ({ ...prev, [stringId]: true }));

      await updateStringName(stringId, newName.trim(), newBrand.trim(), newImage ? newImage.trim() : null);

      setInventory((prevInventory) =>
        prevInventory.map((item) =>
          item.string_id === stringId
            ? { ...item, string_name: newName.trim(), brand: newBrand.trim(), image_url: newImage ? newImage.trim() : null }
            : item
        )
      );

      setEditedNames((prev) => {
        const newValues = { ...prev };
        delete newValues[stringId];
        return newValues;
      });

      setEditedBrands((prev) => {
        const newValues = { ...prev };
        delete newValues[stringId];
        return newValues;
      });

      setEditedImages((prev) => {
        const newValues = { ...prev };
        delete newValues[stringId];
        return newValues;
      });

      setUpdatingName((prev) => ({ ...prev, [stringId]: false }));
    } catch (error) {
      console.error("Error updating string name:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update string name";
      alert(`Failed to update string name: ${errorMsg}`);
      setUpdatingName((prev) => ({ ...prev, [stringId]: false }));
    }
  };

  const handleDeleteString = (string) => {
    setStringToDelete(string);
    setShowDeleteModal(true);
  };

  const confirmDeleteString = async () => {
    if (!stringToDelete) return;
    try {
      setDeletingString(true);
      await deleteString(stringToDelete.string_id);
      setInventory((prevInventory) => prevInventory.filter((item) => item.string_id !== stringToDelete.string_id));
      setSuccessMessage("String deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      setShowDeleteModal(false);
      setStringToDelete(null);
    } catch (error) {
      console.error("Error deleting string:", error);
      alert(error.response?.data?.message || "Failed to delete string");
    } finally {
      setDeletingString(false);
    }
  };

  const cancelDeleteString = () => {
    setShowDeleteModal(false);
    setStringToDelete(null);
  };

  const handleSaveStock = async (stringId) => {
    console.log("Saving stock for stringId:", stringId);
    const newQuantity = editedValues[stringId];
    const quantity = parseInt(newQuantity);

    if (isNaN(quantity) || quantity < 0) {
      alert("Please enter a valid number (0 or greater)");
      return;
    }

    if (!stringId) {
      alert("Error: Invalid string ID");
      console.error("stringId is missing or invalid:", stringId);
      return;
    }

    try {
      setUpdating((prev) => ({ ...prev, [stringId]: true }));

      console.log("Calling updateStock with:", {
        stringId,
        quantity,
        location: userLocation,
      });
      await updateStock(stringId, quantity, userLocation);

      setInventory((prevInventory) =>
        prevInventory.map((item) =>
          item.string_id === stringId
            ? { ...item, remaining_quantity: quantity }
            : item
        )
      );

      setEditedValues((prev) => {
        const newValues = { ...prev };
        delete newValues[stringId];
        return newValues;
      });

      setUpdating((prev) => ({ ...prev, [stringId]: false }));
    } catch (error) {
      console.error("Error updating stock:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update stock";
      alert(`Failed to update stock: ${errorMsg}`);
      setUpdating((prev) => ({ ...prev, [stringId]: false }));
    }
  };

  const handleAddString = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!newString.string_name.trim()) {
      alert("Please enter a string name");
      return;
    }
    if (!newString.brand.trim()) {
      alert("Please enter a brand");
      return;
    }
    if (!newString.cost_per_unit || parseFloat(newString.cost_per_unit) < 0) {
      alert("Please enter a valid cost");
      return;
    }

    try {
      setAddingString(true);
      await addString({
        string_name: newString.string_name.trim(),
        brand: newString.brand.trim(),
        cost_per_unit: parseFloat(newString.cost_per_unit),
        image_url: newString.image_url.trim() || null,
      });

      // Show success message
      setSuccessMessage("String added successfully! Please update the stock for your location below.");
      setTimeout(() => setSuccessMessage(""), 3000);

      // Reset form
      setNewString({
        string_name: "",
        brand: "",
        cost_per_unit: "",
        image_url: "",
      });
      setShowAddForm(false);

      // Refresh inventory
      await fetchInventory();
    } catch (error) {
      console.error("Error adding string:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to add string";
      alert(`Failed to add string: ${errorMsg}`);
    } finally {
      setAddingString(false);
    }
  };

  const filteredInventory = inventory.filter((item) => {
    if (filter === "in_stock") return item.remaining_quantity > 0;
    if (filter === "out_of_stock") return item.remaining_quantity === 0;
    return true;
  });

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>String Inventory</h1>
        <div className={styles.headerActions}>
          {(userRole === "superadmin" || userRole === "admin") && (
            <button
              className={styles.addBtn}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? "Cancel" : "+ Add New String"}
            </button>
          )}
          <div className={styles.filters}>
            <button
              className={`${styles.filterBtn} ${
                filter === "all" ? styles.active : ""
              }`}
              onClick={() => setFilter("all")}
            >
              All Strings
            </button>
            <button
              className={`${styles.filterBtn} ${
                filter === "in_stock" ? styles.active : ""
              }`}
              onClick={() => setFilter("in_stock")}
            >
              In Stock
            </button>
            <button
              className={`${styles.filterBtn} ${
                filter === "out_of_stock" ? styles.active : ""
              }`}
              onClick={() => setFilter("out_of_stock")}
            >
              Out of Stock
            </button>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}

      {showAddForm && (
        <div className={styles.addFormContainer}>
          <h2 className={styles.formTitle}>Add New String</h2>
          <form onSubmit={handleAddString} className={styles.addForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="string_name">String Name *</label>
                <input
                  type="text"
                  id="string_name"
                  value={newString.string_name}
                  onChange={(e) =>
                    setNewString({ ...newString, string_name: e.target.value })
                  }
                  placeholder="Enter String Name"
                  required
                  disabled={addingString}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="brand">Brand *</label>
                <input
                  type="text"
                  id="brand"
                  value={newString.brand}
                  onChange={(e) =>
                    setNewString({ ...newString, brand: e.target.value })
                  }
                  placeholder="Enter Brand Name"
                  required
                  disabled={addingString}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="cost_per_unit">Cost (AED) *</label>
                <input
                  type="number"
                  id="cost_per_unit"
                  min="0"
                  step="0.01"
                  value={newString.cost_per_unit}
                  onChange={(e) =>
                    setNewString({
                      ...newString,
                      cost_per_unit: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  required
                  disabled={addingString}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="image_url">Image URL</label>
                <input
                  type="text"
                  id="image_url"
                  value={newString.image_url}
                  onChange={(e) =>
                    setNewString({
                      ...newString,
                      image_url: e.target.value,
                    })
                  }
                  placeholder="Enter Image URL"
                  disabled={addingString}
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={addingString}
              >
                {addingString ? "Adding..." : "Add String"}
              </button>
            </div>
          </form>
        </div>
      )}

      {filteredInventory.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No strings found matching the selected filter.</p>
        </div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>String Name</th>
                  <th>Brand</th>
                  {(userRole === "superadmin" || userRole === "admin") && <th>Image URL</th>}
                  <th>Cost (AED)</th>
                  {(userRole === "superadmin" || userRole === "admin") ? (
                    <>
                      <th>Jumeirah Stock</th>
                      <th>Dubai Hills Stock</th>
                      <th>Total Stock</th>
                      <th>Actions</th>
                    </>
                  ) : (
                    <th>
                      Current Stock{userLocation ? ` (${userLocation})` : ""}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((item) => (
                  <tr key={item.string_id}>
                    <td>
                      <div className={styles.stockCell}>
                        <input
                          type="text"
                          className={styles.nameInput}
                          value={
                            editedNames[item.string_id] !== undefined
                              ? editedNames[item.string_id]
                              : item.string_name
                          }
                          onChange={(e) =>
                            handleNameChange(item.string_id, e.target.value)
                          }
                          disabled={updatingName[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                        />
                      </div>
                    </td>
                    <td>
                      <div className={styles.stockCell}>
                        <input
                          type="text"
                          className={styles.nameInput}
                          value={
                            editedBrands[item.string_id] !== undefined
                              ? editedBrands[item.string_id]
                              : item.brand || ""
                          }
                          onChange={(e) =>
                            handleBrandChange(item.string_id, e.target.value)
                          }
                          disabled={updatingName[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                        />
                      </div>
                    </td>
                    {(userRole === "superadmin" || userRole === "admin") && (
                      <td>
                        <div className={styles.stockCell}>
                          <input
                            type="text"
                            className={styles.nameInput}
                            value={
                              editedImages[item.string_id] !== undefined
                                ? editedImages[item.string_id]
                                : item.image_url || ""
                            }
                            onChange={(e) =>
                              handleImageChange(item.string_id, e.target.value)
                            }
                            disabled={updatingName[item.string_id]}
                            placeholder="Image URL"
                          />
                          {(editedNames[item.string_id] !== undefined || 
                            editedBrands[item.string_id] !== undefined ||
                            editedImages[item.string_id] !== undefined) && (
                            <button
                              className={styles.saveBtn}
                              onClick={() => handleSaveName(item.string_id)}
                              disabled={updatingName[item.string_id]}
                            >
                              {updatingName[item.string_id]
                                ? "Saving..."
                                : "Save"}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {!(userRole === "superadmin" || userRole === "admin") && (
                      <>
                        {/* Hidden placeholders to trigger saving if brand changes */}
                        {(editedNames[item.string_id] !== undefined || 
                          editedBrands[item.string_id] !== undefined) && (
                          <button
                            className={styles.saveBtn}
                            onClick={() => handleSaveName(item.string_id)}
                            disabled={updatingName[item.string_id]}
                            style={{ display: "none" }}
                          >
                            Save
                          </button>
                        )}
                      </>
                    )}
                    <td>
                      <div className={styles.stockCell}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={styles.costInput}
                          value={
                            editedCosts[item.string_id] !== undefined
                              ? editedCosts[item.string_id]
                              : parseFloat(item.cost_per_unit).toFixed(2)
                          }
                          onChange={(e) =>
                            handleCostChange(item.string_id, e.target.value)
                          }
                          disabled={updatingCost[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                        />
                        {editedCosts[item.string_id] !== undefined && (
                          <button
                            className={styles.saveBtn}
                            onClick={() => handleSaveCost(item.string_id)}
                            disabled={updatingCost[item.string_id]}
                          >
                            {updatingCost[item.string_id]
                              ? "Saving..."
                              : "Save"}
                          </button>
                        )}
                      </div>
                    </td>
                    {(userRole === "superadmin" || userRole === "admin") ? (
                      <>
                        <td>
                          <div className={styles.stockCell}>
                            <input
                              type="number"
                              min="0"
                              className={styles.stockInput}
                              value={
                                editedValues[`${item.string_id}_jumeirah`] !== undefined
                                  ? editedValues[`${item.string_id}_jumeirah`]
                                  : item.jumeriah_stock || 0
                              }
                              onChange={(e) =>
                                handleInputChange(`${item.string_id}_jumeirah`, e.target.value)
                              }
                              disabled={updating[`${item.string_id}_jumeirah`]}
                            />
                            {editedValues[`${item.string_id}_jumeirah`] !== undefined && (
                              <button
                                className={styles.saveBtn}
                                onClick={() => {
                                  const quantity = parseInt(editedValues[`${item.string_id}_jumeirah`]);
                                  if (isNaN(quantity) || quantity < 0) {
                                    alert("Please enter a valid number (0 or greater)");
                                    return;
                                  }
                                  setUpdating((prev) => ({ ...prev, [`${item.string_id}_jumeirah`]: true }));
                                  updateStock(item.string_id, quantity, "Jumeirah Store")
                                    .then(() => {
                                      setInventory((prevInventory) =>
                                        prevInventory.map((i) =>
                                          i.string_id === item.string_id
                                            ? { ...i, jumeriah_stock: quantity, remaining_quantity: (quantity + (i.dubaihills_stock || 0)) }
                                            : i
                                        )
                                      );
                                      setEditedValues((prev) => {
                                        const newValues = { ...prev };
                                        delete newValues[`${item.string_id}_jumeirah`];
                                        return newValues;
                                      });
                                      setUpdating((prev) => ({ ...prev, [`${item.string_id}_jumeirah`]: false }));
                                    })
                                    .catch((error) => {
                                      console.error("Error updating stock:", error);
                                      alert("Failed to update stock");
                                      setUpdating((prev) => ({ ...prev, [`${item.string_id}_jumeirah`]: false }));
                                    });
                                }}
                                disabled={updating[`${item.string_id}_jumeirah`]}
                              >
                                {updating[`${item.string_id}_jumeirah`] ? "Saving..." : "Save"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={styles.stockCell}>
                            <input
                              type="number"
                              min="0"
                              className={styles.stockInput}
                              value={
                                editedValues[`${item.string_id}_dubaihills`] !== undefined
                                  ? editedValues[`${item.string_id}_dubaihills`]
                                  : item.dubaihills_stock || 0
                              }
                              onChange={(e) =>
                                handleInputChange(`${item.string_id}_dubaihills`, e.target.value)
                              }
                              disabled={updating[`${item.string_id}_dubaihills`]}
                            />
                            {editedValues[`${item.string_id}_dubaihills`] !== undefined && (
                              <button
                                className={styles.saveBtn}
                                onClick={() => {
                                  const quantity = parseInt(editedValues[`${item.string_id}_dubaihills`]);
                                  if (isNaN(quantity) || quantity < 0) {
                                    alert("Please enter a valid number (0 or greater)");
                                    return;
                                  }
                                  setUpdating((prev) => ({ ...prev, [`${item.string_id}_dubaihills`]: true }));
                                  updateStock(item.string_id, quantity, "Dubai Hills Store")
                                    .then(() => {
                                      setInventory((prevInventory) =>
                                        prevInventory.map((i) =>
                                          i.string_id === item.string_id
                                            ? { ...i, dubaihills_stock: quantity, remaining_quantity: ((i.jumeriah_stock || 0) + quantity) }
                                            : i
                                        )
                                      );
                                      setEditedValues((prev) => {
                                        const newValues = { ...prev };
                                        delete newValues[`${item.string_id}_dubaihills`];
                                        return newValues;
                                      });
                                      setUpdating((prev) => ({ ...prev, [`${item.string_id}_dubaihills`]: false }));
                                    })
                                    .catch((error) => {
                                      console.error("Error updating stock:", error);
                                      alert("Failed to update stock");
                                      setUpdating((prev) => ({ ...prev, [`${item.string_id}_dubaihills`]: false }));
                                    });
                                }}
                                disabled={updating[`${item.string_id}_dubaihills`]}
                              >
                                {updating[`${item.string_id}_dubaihills`] ? "Saving..." : "Save"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={styles.stockCell}>
                            <strong>{item.remaining_quantity || 0}</strong>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteString(item)}
                            className={styles.saveBtn}
                            style={{ backgroundColor: "#e08a78" }}
                          >
                            Delete
                          </button>
                        </td>
                      </>
                    ) : (
                      <td>
                        <div className={styles.stockCell}>
                          <input
                            type="number"
                            min="0"
                            className={styles.stockInput}
                            value={
                              editedValues[item.string_id] !== undefined
                                ? editedValues[item.string_id]
                                : item.remaining_quantity
                            }
                            onChange={(e) =>
                              handleInputChange(item.string_id, e.target.value)
                            }
                            disabled={updating[item.string_id]}
                          />
                          {editedValues[item.string_id] !== undefined && (
                            <button
                              className={styles.saveBtn}
                              onClick={() => handleSaveStock(item.string_id)}
                              disabled={updating[item.string_id]}
                            >
                              {updating[item.string_id] ? "Saving..." : "Save"}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileCardView}>
            {filteredInventory.map((item) => (
              <div key={item.string_id} className={styles.inventoryCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardStringName}>String Details</div>
                </div>

                <div className={styles.cardRow}>
                  <div className={styles.cardLabel}>String Name</div>
                  <div className={styles.cardInputGroup}>
                    <input
                      type="text"
                      className={styles.cardInput}
                      value={
                        editedNames[item.string_id] !== undefined
                          ? editedNames[item.string_id]
                          : item.string_name
                      }
                      onChange={(e) =>
                        handleNameChange(item.string_id, e.target.value)
                      }
                      disabled={updatingName[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                      placeholder="String Name"
                    />
                  </div>
                </div>

                <div className={styles.cardRow}>
                  <div className={styles.cardLabel}>Brand</div>
                  <div className={styles.cardInputGroup}>
                    <input
                      type="text"
                      className={styles.cardInput}
                      value={
                        editedBrands[item.string_id] !== undefined
                          ? editedBrands[item.string_id]
                          : item.brand || ""
                      }
                      onChange={(e) =>
                        handleBrandChange(item.string_id, e.target.value)
                      }
                      disabled={updatingName[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                      placeholder="Brand"
                    />
                    {(editedNames[item.string_id] !== undefined || 
                      editedBrands[item.string_id] !== undefined) && (
                      <button
                        className={styles.cardSaveBtn}
                        onClick={() => handleSaveName(item.string_id)}
                        disabled={updatingName[item.string_id]}
                      >
                        {updatingName[item.string_id] ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.cardRow}>
                  <div className={styles.cardLabel}>Cost</div>
                  <div className={styles.cardInputGroup}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.cardInput}
                      value={
                        editedCosts[item.string_id] !== undefined
                          ? editedCosts[item.string_id]
                          : parseFloat(item.cost_per_unit).toFixed(2)
                      }
                      onChange={(e) =>
                        handleCostChange(item.string_id, e.target.value)
                      }
                      disabled={updatingCost[item.string_id] || !(userRole === "superadmin" || userRole === "admin")}
                      placeholder="Cost (AED)"
                    />
                    {editedCosts[item.string_id] !== undefined && (
                      <button
                        className={styles.cardSaveBtn}
                        onClick={() => handleSaveCost(item.string_id)}
                        disabled={updatingCost[item.string_id]}
                      >
                        {updatingCost[item.string_id] ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.cardRow}>
                  <div className={styles.cardLabel}>Stock</div>
                  <div className={styles.cardInputGroup}>
                    <input
                      type="number"
                      min="0"
                      className={styles.cardInput}
                      value={
                        editedValues[item.string_id] !== undefined
                          ? editedValues[item.string_id]
                          : item.remaining_quantity
                      }
                      onChange={(e) =>
                        handleInputChange(item.string_id, e.target.value)
                      }
                      disabled={updating[item.string_id]}
                      placeholder="Quantity"
                    />
                    {editedValues[item.string_id] !== undefined && (
                      <button
                        className={styles.cardSaveBtn}
                        onClick={() => handleSaveStock(item.string_id)}
                        disabled={updating[item.string_id]}
                      >
                        {updating[item.string_id] ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
            {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Delete String</h2>
              <button onClick={cancelDeleteString} className={styles.closeBtn}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.deleteWarning}>
                ⚠️ Are you sure you want to delete this string from inventory?
              </p>
              <div className={styles.deleteDetails}>
                <p>String Name: <strong>{stringToDelete?.string_name}</strong></p>
                <p>Brand: <strong>{stringToDelete?.brand}</strong></p>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button onClick={cancelDeleteString} className={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={confirmDeleteString} className={styles.deleteConfirmBtn} disabled={deletingString}>
                {deletingString ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StringerInventory;
